import { Buffer } from "buffer";
import * as anchor from "@coral-xyz/anchor";
import { AnchorProvider, Program } from "@coral-xyz/anchor";
import bs58 from "bs58";
import { Keypair, PublicKey } from "@solana/web3.js";
import { sha256 } from "js-sha256";
import { generatePDA } from "./pda";
import { fetchAllFartSubmissions, fetchFartSubmissionsForChampionship, fetchSubmissionsWithVotes } from "./submission";
import { uuidBs58 } from "./uuid";
import { Fartlab } from "idl/types/fartlab";
import { BN } from "bn.js";

// PDA seed for championship
const CHAMPIONSHIP_PDA_SEED = "championship";

/**
 * Creates a new championship
 */
export async function createChampionship(program: Program<Fartlab>, wallet: Keypair, championshipData: any) {
  const uuid = championshipData.uuid || uuidBs58();
  const name = championshipData.name || "Fart Championship";
  const description = championshipData.description || "The ultimate fart competition!";
  const startTime = championshipData.startTime || new BN(Math.floor(Date.now() / 1000)); // Current time
  const endTime = championshipData.endTime || startTime.add(new BN(86400 * 3)); // 3 days later
  const maxVotesPerUser = championshipData.allowPreviousFarts || 10000;
  const maxFarts = championshipData.maxFarts || 10;
  const maxFartsUser = championshipData.maxFartsUser || 5;
  const minParticipants = championshipData.minParticipants || 5;
  const isActive = championshipData.isActive || true;
  const admins = [wallet.publicKey];
  try {
    const tx = await program.methods
      .createChampionship(uuid, name, description, startTime, endTime, maxVotesPerUser, maxFarts, maxFartsUser, minParticipants, "", isActive, admins)
      .accounts({
        owner: wallet.publicKey,
      })
      .signers([wallet])
      .rpc();

    console.log("✅ Championship created! Transaction:", tx);
    return { success: true, tx };
  } catch (error) {
    console.error("❌ Championship creation failed:", error);
    return {
      success: false,
      error: {
        message: error?.error?.errorMessage || "Unknown error occurred.",
        code: error?.error?.errorCode?.number || null,
        logs: error?.logs || [],
        programErrorStack: error?._programErrorStack?.stack || [],
      },
    };
  }
}

/**
 * Fetches all submissions for a championship and ranks them by votes.
 */
export async function getChampionshipResults(program: Program<Fartlab>, provider: AnchorProvider, championshipUuid: string) {
    console.log(`🔍 Fetching results for Championship: ${championshipUuid}`);

    // ✅ Fetch submissions and their votes
    const submissionsWithVotes = await fetchSubmissionsWithVotes(program, provider, championshipUuid);

    // ✅ Sort by vote count (Descending)
    submissionsWithVotes.sort((a, b) => b.voteCount - a.voteCount);

    console.log("🏆 Championship Results:", submissionsWithVotes);

    return {
        winner: submissionsWithVotes[0] || null,
        secondPlace: submissionsWithVotes[1] || null,
        thirdPlace: submissionsWithVotes[2] || null,
        rankings: submissionsWithVotes,
    };
}


export async function printChampionshipsWithSubmissions(program: Program<Fartlab>, provider: AnchorProvider) {
  console.log("🔄 Fetching all championships...");
  const championships = await fetchAllChampionships(program, provider);
  console.log(`🏆 Found ${championships.length} championships`);

  console.log("🔄 Fetching all fart submissions...");
  const submissions = await fetchAllFartSubmissions(program, provider);
  console.log(`💨 Found ${submissions.length} fart submissions`);

  // ✅ Map each championship with its corresponding submissions
  championships.forEach((championship) => {
    const championshipUuid = championship.uuid;
    const relatedSubmissions = submissions.filter((sub) => sub.championshipUuid === championshipUuid);

    console.log("\n🏆 Championship:");
    console.log(`  🆔 UUID: ${championshipUuid}`);
    console.log(`  🏷  Name: ${championship.name}`);
    console.log(`  📜 Description: ${championship.description}`);
    console.log(`  🔛 Active: ${championship.isActive ? "Yes" : "No"}`);
    console.log(`  📅 Start Time: ${new Date(championship.startTime * 1000)}`);
    console.log(`  ⏳ End Time: ${new Date(championship.endTime * 1000)}`);

    if (relatedSubmissions.length > 0) {
      console.log("  💨 Fart Submissions:");
      relatedSubmissions.forEach((submission, index) => {
        console.log(`    ${index + 1}. 🆔 Fart UUID: ${submission.fartUuid}`);
        console.log(`       👤 Owner: ${new PublicKey(submission.user).toBase58()}`);
        console.log(`       🏷  Title: ${submission.title}`);
        console.log(`       📜 Description: ${submission.description}`);
        console.log(`       ⏳ Submitted At: ${new Date(submission.submittedAt * 1000)}`);
      });
    } else {
      console.log("  ❌ No fart submissions found for this championship.");
    }
  });
}

/**
 * Checks if a user can submit a fart to a championship.
 *
 * @param {Program<Fartlab>} program - The Anchor program instance.
 * @param {string} championshipUuid - The championship UUID.
 * @param {string} userId - The user's public key (Base58).
 * @param {anchor.BN} submittedAt - Submission timestamp (Unix).
 * @returns {Promise<boolean>} - Whether the user can submit.
 */
export async function canUserSubmitToChampionship(
  program: Program<Fartlab>,
  provider: AnchorProvider,
  championshipUuid: string,
  userId: string,
  submittedAt: anchor.BN
): Promise<boolean> {
  console.log(`🔍 Checking if user ${userId} can submit to Championship: ${championshipUuid}`);

  // ✅ Fetch championship data using `getChampionship`
  const championship = await getChampionship(program, championshipUuid);
  
  if (!championship) {
    console.error("❌ Championship not found.");
    return false;
  }

  console.log("🏆 Championship Info:", {
    name: championship.name,
    isActive: championship.isActive,
    startTime: championship.startTime,
    endTime: championship.endTime,
    maxFarts: championship.maxFarts,
    maxFartsUser: championship.maxFartsUser,
  });

  // ✅ Check if the championship is active
  if (!championship.isActive) {
    console.log("❌ Championship is not active.");
    return false;
  }

  // ✅ Check if the submission time is within the championship's allowed period
  if (submittedAt < championship.startTime || submittedAt > championship.endTime) {
    console.log("❌ Submission is outside the allowed time window.");
    return false;
  }

  // ✅ Fetch all submissions for this championship (all + user-specific in one call)
  const allSubmissions = await fetchFartSubmissionsForChampionship(program, provider, championshipUuid);
  const userSubmissions = await fetchFartSubmissionsForChampionship(program, provider, championshipUuid, userId);

  // ✅ Check if max farts limit has been reached
  if (championship.maxFarts && allSubmissions.length >= championship.maxFarts) {
    console.log("❌ Championship has reached the maximum number of submissions.");
    return false;
  }

  // ✅ Check if the user has exceeded their personal max submission limit
  if (championship.maxFartsUser && userSubmissions.length >= championship.maxFartsUser) {
    console.log("❌ User has reached the max allowed submissions for this championship.");
    return false;
  }

  console.log("✅ User is allowed to submit!");
  return true;
}


/**
 * Fetches all championships from the blockchain
*/
export async function fetchAllChampionships(program: Program<Fartlab>, provider: AnchorProvider = anchor.AnchorProvider.env()) {
    const programId = program.programId;
    // ✅ Get the discriminator for "Championship"
    // const discriminator = anchor.utils.bytes.bs58.encode(Buffer.from(anchor.utils.sha256.hash("account:Championship").slice(0, 8)));
    const discriminator = bs58.encode(Buffer.from(sha256.digest("account:Championship")).subarray(0, 8));

    const accounts = await provider.connection.getProgramAccounts(programId, {
        filters: [
        {
            memcmp: {
            offset: 0, // ✅ Ensure first bytes match Championship struct
            bytes: discriminator,
            },
        },
        // {
        //   dataSize: 1024, // Adjust based on Championship struct size
        // },
        ],
    });

    const championships = accounts.map((acc) => program.coder.accounts.decode("championship", acc.account.data));
    // console.log("🏆 Fetched Championships:", championships);
    // console.log("🏆 Fetched Championships:", accounts);
    return championships;
}

/**
 * Fetches all championships from the blockchain
*/
export async function getChampionship(program: Program<Fartlab>, uuid: string) {
  // ✅ Derive the PDA using the correct seeds
  const championshipPDA = await generatePDA(program, CHAMPIONSHIP_PDA_SEED, uuid);
  console.log(`🔍 Fetching Championship at PDA: ${new PublicKey(championshipPDA).toBase58()}`);
  // ✅ Fetch the championship account
  const championship = await program.account.championship.fetch(championshipPDA);

  return championship;
}


// ✅ Utility function: Get number of championships
export async function getChampionshipCount(program: Program<Fartlab>,  provider: AnchorProvider) {
    try {
        const championships = await fetchAllChampionships(program, provider);
        console.log(`🏆 Current Championships Count: ${championships.length}`);
        return championships.length;
    } catch (error) {
        console.error("❌ Failed to fetch championships:", error);
        throw error;
    }
}

/**
 * Fetches the latest championship
 */
export async function fetchLatestChampionship(program: Program<Fartlab>,  provider: AnchorProvider) {
  const championships = await fetchAllChampionships(program, provider);
  if (championships.length === 0) return null;
  return championships.reduce((prev, current) => (prev.start_time > current.start_time ? prev : current));
}

/**
 * Deletes a championship
 */
export async function deleteChampionship(program: Program<Fartlab>, wallet: Keypair, uuid: string) {
  try {
    const owner = wallet.publicKey; // The owner must match PDA derivation

    // ✅ Derive the Championship PDA (Must match Rust)
    const championshipPDA = await generatePDA(program, CHAMPIONSHIP_PDA_SEED, uuid);

    //   console.log(`🔍 Deleting Championship at PDA: ${championshipPDA.toBase58()}`);
    const tx = await program.methods
      .deleteChampionship()
      .accounts({
        championship: championshipPDA,
        owner: wallet.publicKey,
      })
      .signers([wallet])
      .rpc();

    console.log(`🗑️ Championship deleted! Transaction: ${tx}`);
    return tx;
  } catch (error) {
    console.error(`❌ Failed to delete championship:`, error);
    throw error;
  }
    
}

export async function generateChampionshipPDA(
  program: Program<Fartlab>,
  uuid: string, // UUID of the championship
) {
    // ✅ Derive the Championship PDA (Must match Rust)
    const [championshipPDA] = anchor.web3.PublicKey.findProgramAddressSync(
      [
        Buffer.from("championship"), // ✅ Matches `b"championship"`
        Buffer.from(uuid), // ✅ Matches `uuid.as_bytes()`
        // owner.publicKey.toBuffer(), // ✅ Matches `owner.key().as_ref()`
      ],
      program.programId
    );
    return championshipPDA;
};

/**
 * Updates a championship (Only Admins can update)
 */
export async function updateChampionship(
  program: Program<Fartlab>,
  wallet: Keypair,
  uuid: string, // Name of the championship
  newName: string, // ✅ New name for the championship
  description?: string,
  startTime?: number,
  endTime?: number,
  maxVotesPerUser?: number,
  maxFarts?: number,
  isActive?: boolean,
) {
  try {
    // ✅ Derive the Championship PDA (Must match Rust)
    const championshipPDA = await generatePDA(program, CHAMPIONSHIP_PDA_SEED, uuid);

    console.log(`🔍 Updating Championship at PDA: ${new PublicKey(championshipPDA).toBase58()}`);

    // ✅ Call `updateChampionship` with correct account mapping
    const tx = await program.methods
      .updateChampionship(
        newName, // ✅ Pass new name
        description || null,
        startTime ? new BN(startTime) : null,
        endTime ? new BN(endTime) : null,
        maxVotesPerUser ?? null,
        maxFarts !== undefined ? maxFarts : null,
        isActive ? isActive: null, // ✅ Only update if provided
      )
      .accounts({
        championship: championshipPDA, // ✅ Derived PDA
        owner: wallet.publicKey, // ✅ Must be an admin
      })
      .signers([wallet])
      .rpc();

    console.log(`🏆 Championship updated! Transaction: ${tx}`);
    return tx;
  } catch (error) {
    console.error(`❌ Failed to update championship:`, error);
    throw error;
  }
}
